import {convertJSONToDrawing} from '@application/helper/drawing/convert-json-to-drawing';
import {Drawing} from '@domain/production-schedule/drawing';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {WeaveStructure} from '@domain/textile-data/weave-structure/weave-structure';
import {OverviewListColoredYarnSet} from '@presentation/pages/textile-data/colored-yarn-set/overview/overview-list-colored-yarn-set';
import {VersionHistory} from './version-history';

export class ProductConfiguration {
  private _id: number;
  private _name: string;
  private _machine: MachineSummary;
  private _quality: WeaveQuality;
  private _helpWeaveStructures: WeaveStructure[];
  private _designs: Drawing[];
  private _coloredYarnSet: OverviewListColoredYarnSet;
  private _weftColoredYarnSet: OverviewListColoredYarnSet;
  private _versionHistories: VersionHistory[];
  private _locked: boolean;
  private _description: string;

  public constructor(
    id: number,
    name: string,
    machine: MachineSummary,
    quality: WeaveQuality,
    helpWeaveStructures: WeaveStructure[],
    designs: Drawing[],
    coloredYarnSet: OverviewListColoredYarnSet,
    weftColoredYarnSet: OverviewListColoredYarnSet,
    versionHistories: VersionHistory[],
    locked: boolean,
    description: string
  ) {
    this._id = id;
    this._name = name;
    this._machine = machine;
    this._quality = quality;
    this._helpWeaveStructures = helpWeaveStructures;
    this._designs = designs;
    this._coloredYarnSet = coloredYarnSet;
    this._weftColoredYarnSet = weftColoredYarnSet;
    this._versionHistories = versionHistories;
    this._locked = locked;
    this._description = description;
  }

  public get id(): number {
    return this._id;
  }

  public set id(id: number) {
    this._id = id;
  }

  public get name(): string {
    return this._name;
  }

  public set name(name: string) {
    this._name = name;
  }

  public get machine(): MachineSummary {
    return this._machine;
  }

  public set machine(value: MachineSummary) {
    this._machine = value;
  }

  public get quality(): WeaveQuality {
    return this._quality;
  }

  public get helpWeaveStructures(): WeaveStructure[] {
    return this._helpWeaveStructures;
  }

  public set helpWeaveStructure(image: WeaveStructure[]) {
    this._helpWeaveStructures = image;
  }

  public get designs(): Drawing[] {
    return this._designs;
  }

  public set designs(designs: Drawing[]) {
    this._designs = designs;
  }

  public get weftColoredYarnSet(): OverviewListColoredYarnSet {
    return this._weftColoredYarnSet;
  }

  public set weftColoredYarnSet(weftColoredYarnSet: OverviewListColoredYarnSet) {
    this._weftColoredYarnSet = weftColoredYarnSet;
  }

  public get coloredYarnSet(): OverviewListColoredYarnSet {
    return this._coloredYarnSet;
  }

  public set coloredYarnSet(coloredYarnSet: OverviewListColoredYarnSet) {
    this._coloredYarnSet = coloredYarnSet;
  }

  public get versionHistories(): VersionHistory[] {
    return this._versionHistories;
  }

  public set versionHistories(versionHistories: VersionHistory[]) {
    this._versionHistories = versionHistories;
  }

  public get locked(): boolean {
    return this._locked;
  }

  public set locked(locked: boolean) {
    this._locked = locked;
  }

  public get description(): string {
    return this._description;
  }

  public set description(description: string) {
    this._description = description;
  }

  public static fromJSON(productConfigurationJSON: any): ProductConfiguration {
    const designs: Drawing[] = productConfigurationJSON?.drawings.map((design: Drawing) => {
      return convertJSONToDrawing(design);
    });
    const helpWeaveStructures: WeaveStructure[] = productConfigurationJSON?.weaveStructures.map((weaveStructure: WeaveStructure) => {
      return WeaveStructure.fromJSON(weaveStructure);
    });

    return new ProductConfiguration(
      productConfigurationJSON.id,
      productConfigurationJSON.name,
      productConfigurationJSON.machine ? MachineSummary.fromJSON(productConfigurationJSON.machine) : null,
      WeaveQuality.fromJSON(productConfigurationJSON.quality),
      helpWeaveStructures,
      designs,
      productConfigurationJSON.coloredYarnSet ? OverviewListColoredYarnSet.fromJSON(productConfigurationJSON.coloredYarnSet) : null,
      productConfigurationJSON.weftColoredYarnSet ? OverviewListColoredYarnSet.fromJSON(productConfigurationJSON.weftColoredYarnSet) : null,
      productConfigurationJSON.versionHistories.map((versionHistory: VersionHistory) => VersionHistory.fromJSON(versionHistory)),
      productConfigurationJSON.locked ?? false,
      productConfigurationJSON.description
    );
  }

  public toJSON(): JSON {
    return {
      id: this._id,
      name: this._name,
      equipmentId: this._machine.id,
      qualityId: this._quality.id,
      weaveStructureIds: this._helpWeaveStructures ? this._helpWeaveStructures.map((helpWeaveStructure: WeaveStructure) => helpWeaveStructure.id) : [],
      designIds: this._designs ? this._designs.map((design: Drawing) => design.id) : [],
      coloredYarnSetId: this._coloredYarnSet?.id,
      weftColoredYarnSetId: this._weftColoredYarnSet?.id,
      locked: this.locked,
      description: this.description
    } as any as JSON;
  }
}
